import { useState } from 'react';

import { Box, Button, ButtonGroup, Modal, TextButton, Toast, useToast } from '@zillow/constellation-10';

import { SmartConfigMode } from '@zg-rentals/ts-rental-manager-api-client';
import { useRentalManagerUserPropertySpotlightWebApiHandler_setSmartConfigMode } from '@zg-rentals/ts-rental-manager-api-client-react-query';

import { SPOTLIGHT_COPY as COPY } from './spotlightCopy';

const MODAL_COPY = COPY.smartAssign.modal;

// Constellation v10 semantic tokens (panda CSS vars), so the modal stays theme-safe. These native
// elements can't take the `css` prop, so we reference the token variables directly.
const C = {
  blueTint: 'var(--colors-blue-50)',
  blue100: 'var(--colors-bg-accent-blue-soft)',
  blue600: 'var(--colors-border-accent-blue-strong)',
  blueFill: 'var(--colors-bg-accent-blue-impact)',
  blue700: 'var(--colors-text-accent-blue-strong)',
  gray50: 'var(--colors-bg-softest)',
  gray100: 'var(--colors-bg-softer)',
  borderDefault: 'var(--colors-border-neutral)',
  borderStrong: 'var(--colors-border-neutral)',
  fgNeutral: 'var(--colors-text-neutral)',
  fgSubtle: 'var(--colors-text-subtle)',
  fgMuted: 'var(--colors-text-subtle)',
  green600: 'var(--colors-text-feedback-success-hero)',
  white: 'var(--colors-bg-neutral)',
};

function OptionCard({
  selected,
  tag,
  title,
  desc,
  note,
  onSelect,
  testId,
}: {
  selected: boolean;
  tag: string;
  title: string;
  desc: string;
  note: string;
  onSelect: () => void;
  testId: string;
}) {
  const [focused, setFocused] = useState(false);
  return (
    <label
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 14,
        padding: '16px 18px',
        border: `2px solid ${selected ? C.blue600 : C.borderDefault}`,
        borderRadius: 14,
        background: selected ? C.blueTint : C.white,
        cursor: 'pointer',
        textAlign: 'left',
        width: '100%',
        outline: focused ? `2px solid ${C.blue600}` : undefined,
        outlineOffset: 2,
      }}
    >
      <input
        type="radio"
        name="smart-assign-mode"
        checked={selected}
        onChange={onSelect}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        aria-label={title}
        data-testid={testId}
        style={{ position: 'absolute', width: 1, height: 1, margin: 0, opacity: 0 }}
      />
      <span
        aria-hidden="true"
        style={{
          width: 20,
          height: 20,
          borderRadius: '50%',
          border: `1.5px solid ${selected ? C.blue600 : C.borderStrong}`,
          background: selected ? C.blueFill : C.white,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
          marginTop: 1,
        }}
      >
        {selected && <span style={{ width: 8, height: 8, borderRadius: '50%', background: C.white }} />}
      </span>
      <div>
        <div style={{ marginBottom: 5 }}>
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              height: 19,
              padding: '0 8px',
              borderRadius: 999,
              background: selected ? C.blue100 : C.gray100,
              color: selected ? C.blue700 : C.fgMuted,
              fontSize: 10.5,
              fontWeight: 700,
              letterSpacing: '0.03em',
              textTransform: 'uppercase',
              whiteSpace: 'nowrap',
            }}
          >
            {tag}
          </span>
        </div>
        <div style={{ fontSize: 14, fontWeight: 700, color: C.fgNeutral, marginBottom: 4 }}>{title}</div>
        <div style={{ fontSize: 13, color: C.fgSubtle, lineHeight: 1.45 }}>{desc}</div>
        <div style={{ display: 'flex', gap: 6, marginTop: 7, fontSize: 12, color: C.fgMuted, lineHeight: 1.4 }}>
          <span style={{ flexShrink: 0 }}>+</span>
          <span>{note}</span>
        </div>
      </div>
    </label>
  );
}

export function SmartAssignActivationModal({
  open,
  onClose,
  inUse,
  total,
}: {
  open: boolean;
  onClose: () => void;
  /** Placements already occupied — these become pinned when Smart Assign turns on. */
  inUse: number;
  /** Total contracted placements. */
  total: number;
}) {
  const { enqueueToast } = useToast();
  const [selected, setSelected] = useState<SmartConfigMode | null>(null);
  const { mutateAsync: saveSmartConfigMode, isPending } =
    useRentalManagerUserPropertySpotlightWebApiHandler_setSmartConfigMode({
      meta: {
        invalidates: ['getProperties', 'spotlightOverview', 'optimizationConfig'],
        context: { api: 'setSmartConfigMode', details: 'turn on smart assign' },
      },
    });

  const fills = Math.max(0, total - inUse);

  const handleClose = () => {
    setSelected(null);
    onClose();
  };

  const handleConfirm = () => {
    if (!selected) {
      return;
    }
    void saveSmartConfigMode({ postData: { autoOptimizeEnabled: true, smartConfigMode: selected } })
      .then(() => {
        enqueueToast(
          <Toast id="smart-assign-saved" tone="success">
            {MODAL_COPY.savedToast}
          </Toast>,
        );
        handleClose();
        return;
      })
      .catch(() => {
        enqueueToast(
          <Toast id="smart-assign-error" tone="critical">
            {MODAL_COPY.errorToast}
          </Toast>,
        );
        return;
      });
  };

  return (
    <Modal.Root open={open} onOpenChange={(next) => !next && handleClose()} size={{ base: 'fullScreen', md: 'sm' }}>
      <Modal.Portal>
        <Modal.Backdrop>
          <Modal.Content data-panda-theme="zillow" style={{ maxWidth: 480, width: 480, paddingBlockStart: 0 }}>
            <div style={{ padding: '24px 28px 0' }}>
              <h2
                style={{
                  margin: '0 0 8px',
                  fontSize: 22,
                  fontWeight: 900,
                  letterSpacing: '-0.01em',
                  color: C.fgNeutral,
                }}
              >
                {MODAL_COPY.title}
              </h2>
              <p style={{ margin: '0 0 20px', fontSize: 14, color: C.fgSubtle, lineHeight: 1.55 }}>
                {MODAL_COPY.description.lead}
                <strong style={{ color: C.fgNeutral }}>{MODAL_COPY.description.bold}</strong>
                {MODAL_COPY.description.tail}
              </p>
              <div
                style={{
                  display: 'flex',
                  gap: 9,
                  padding: '11px 13px',
                  marginBottom: 18,
                  background: C.gray50,
                  borderRadius: 10,
                  fontSize: 12.5,
                  lineHeight: 1.45,
                  color: C.fgSubtle,
                }}
              >
                <span style={{ flexShrink: 0, marginTop: 1, color: C.green600, fontWeight: 700 }}>✓</span>
                {total === 0 ? (
                  <span>Smart Assign will fill your Spotlight placements as they become available.</span>
                ) : fills === 0 ? (
                  <span>
                    <strong style={{ color: C.fgNeutral }}>{inUse}</strong> of your{' '}
                    <strong style={{ color: C.fgNeutral }}>{total}</strong> placements are already in use. Those
                    listings become pinned; Smart Assign has no free placements to fill right now.
                  </span>
                ) : (
                  <span>
                    <strong style={{ color: C.fgNeutral }}>{inUse}</strong> of your{' '}
                    <strong style={{ color: C.fgNeutral }}>{total}</strong> placements are already in use. Those
                    listings become pinned; Smart Assign fills the other{' '}
                    <strong style={{ color: C.fgNeutral }}>{fills}</strong>.
                  </span>
                )}
              </div>
              <div
                style={{
                  fontSize: 12,
                  fontWeight: 700,
                  letterSpacing: '0.04em',
                  textTransform: 'uppercase',
                  color: C.fgMuted,
                  marginBottom: 10,
                }}
              >
                {MODAL_COPY.selectLabel}
              </div>
              <Box
                role="radiogroup"
                aria-label={MODAL_COPY.selectLabel}
                css={{ display: 'flex', flexDirection: 'column', gap: '200' }}
              >
                <OptionCard
                  testId="smart-assign-all-included"
                  selected={selected === SmartConfigMode.ALL_INCLUDED}
                  onSelect={() => setSelected(SmartConfigMode.ALL_INCLUDED)}
                  tag={MODAL_COPY.allIncluded.tag}
                  title={MODAL_COPY.allIncluded.title}
                  desc={MODAL_COPY.allIncluded.desc}
                  note={MODAL_COPY.allIncluded.footnote}
                />
                <OptionCard
                  testId="smart-assign-all-excluded"
                  selected={selected === SmartConfigMode.ALL_EXCLUDED}
                  onSelect={() => setSelected(SmartConfigMode.ALL_EXCLUDED)}
                  tag={MODAL_COPY.allExcluded.tag}
                  title={MODAL_COPY.allExcluded.title}
                  desc={MODAL_COPY.allExcluded.desc}
                  note={MODAL_COPY.allExcluded.footnote}
                />
              </Box>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '20px 28px 24px' }}>
              <ButtonGroup aria-label="Actions" css={{ width: '100%', justifyContent: 'space-between' }}>
                <TextButton tone="brand" onClick={handleClose}>
                  {MODAL_COPY.cancel}
                </TextButton>
                <Button
                  tone="brand"
                  emphasis="filled"
                  size="md"
                  disabled={!selected || isPending}
                  onClick={handleConfirm}
                >
                  {MODAL_COPY.confirm}
                </Button>
              </ButtonGroup>
            </div>
          </Modal.Content>
        </Modal.Backdrop>
      </Modal.Portal>
    </Modal.Root>
  );
}

export default SmartAssignActivationModal;
